import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { idrFull, num } from './dashUtils';

const MONEY = new Set(['total', 'balance', 'amount']);
const STATE = { paid: 'bg-emerald-50 text-emerald-700', partial: 'bg-amber-50 text-amber-700', open: 'bg-rose-50 text-rose-700', cancelled: 'bg-slate-100 text-slate-600', delivered: 'bg-emerald-50 text-emerald-700', shipped: 'bg-violet-50 text-violet-700', processing: 'bg-amber-50 text-amber-700', pending_payment: 'bg-slate-100 text-slate-600', won: 'bg-emerald-50 text-emerald-700', lost: 'bg-rose-50 text-rose-700', completed: 'bg-emerald-50 text-emerald-700', approved: 'bg-sky-50 text-sky-700', pending: 'bg-amber-50 text-amber-700', rejected: 'bg-rose-50 text-rose-700' };

const cell = (row, key) => {
  const v = row[key];
  if (key === 'state') return <Badge variant="outline" className={`rounded-full ${STATE[v] || ''}`}>{v}</Badge>;
  if (MONEY.has(key)) return <b>{idrFull(v)}</b>;
  if (typeof v === 'number') return num(v);
  if (v === null || v === undefined) return '—';
  return String(v);
};

/** Generic drill-down table fed by GET /dashboard/drill. */
export const DrillDialog = ({ companyId, req, periodQuery, filters, lang = 'id', onClose }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!req) return;
    setData(null);
    api.get(`/companies/${companyId}/dashboard/drill`, { params: { kind: req.kind, ref: req.ref || undefined, ...periodQuery, ...filters } }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [req]); // eslint-disable-line
  if (!req) return null;
  const total = data?.rows?.reduce((a, r) => a + Number(r.total ?? r.balance ?? r.amount ?? 0), 0) || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] w-[min(960px,calc(100vw-24px))] max-w-none overflow-hidden rounded-2xl p-0" data-testid="dash-drill-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pr-6">
            <span>{req.title || data?.title || '…'}{data && <span className="ml-2 text-sm font-normal text-muted-foreground">{data.rows.length} {lang === 'id' ? 'baris' : 'rows'}{total ? ` · ${idrFull(total)}` : ''}</span>}</span>
            {data?.link && <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate(data.link)} data-testid="dash-drill-open-module"><ExternalLink size={14} className="mr-1" />{lang === 'id' ? 'Buka modul' : 'Open module'}</Button>}
          </DialogTitle>
          <DialogDescription className="sr-only">Drill-down</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-auto px-5 pb-5">
          {!data ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" /></div> : data.rows.length === 0 ? <div className="py-12 text-center text-sm text-muted-foreground">{lang === 'id' ? 'Tidak ada data.' : 'No data.'}</div> : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">{data.columns.map(([k, l]) => <th key={k} className={`py-2 pr-3 ${MONEY.has(k) || k === 'qty' || k === 'age' ? 'text-right' : ''}`}>{l}</th>)}</tr></thead>
              <tbody>{data.rows.map((r, i) => <tr key={r.id || i} className={`border-b border-border/50 ${r.link ? 'cursor-pointer hover:bg-muted/40' : ''}`} onClick={() => r.link && navigate(r.link)} data-testid={`dash-drill-row-${i}`}>{data.columns.map(([k]) => <td key={k} className={`py-2 pr-3 ${MONEY.has(k) || k === 'qty' || k === 'age' ? 'text-right whitespace-nowrap' : ''}`}>{cell(r, k)}</td>)}</tr>)}</tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
